import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../Navbar";
import { getPayoutAccount, savePayoutAccount } from "../../services/treatApi";
import "../Pro/Pro.css";
import "./Treat.css";

// =====================================================
// SET UP PAYOUTS — blogger's bank details  (Step 11)  — /treat-payouts
//
// The full account number is sent once, over HTTPS, and stored encrypted on
// the server; this page only ever shows it back masked. Every submission goes
// to the admin's Payout Verification queue (Pending → Verified / Rejected).
// =====================================================

const EMPTY = { accountHolderName: "", accountNumber: "", confirmAccountNumber: "", ifsc: "", phone: "" };

const STATUS_TEXT = {
  pending: "Pending verification — an admin will review your details.",
  verified: "Verified — you can receive payouts.",
  rejected: "Rejected — please correct your details and submit again.",
};

function TreatPayoutSetup() {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    getPayoutAccount()
      .then((res) => {
        setAccount(res.account);
        setEditing(!res.account);
        if (res.account) {
          setForm({ ...EMPTY, accountHolderName: res.account.accountHolderName, ifsc: res.account.ifsc, phone: res.account.phone });
        }
      })
      .catch((err) => setError(err?.response?.data?.message || "Failed to load your payout details"))
      .finally(() => setLoading(false));
  }, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.accountNumber !== form.confirmAccountNumber) {
      setError("Account numbers do not match");
      return;
    }
    setSaving(true);
    try {
      const res = await savePayoutAccount({ ...form, ifsc: form.ifsc.trim().toUpperCase() });
      setAccount(res.account);
      setEditing(false);
      setForm((f) => ({ ...f, accountNumber: "", confirmAccountNumber: "" }));
      setToast(res.message || "Submitted.");
      setTimeout(() => setToast(null), 3500);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save your details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pro-page-wrap">
      <Navbar />
      {toast && <div className="pro-toast pro-toast-success">{toast}</div>}
      <div className="pro-container pro-container-narrow">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">🏦 SET UP PAYOUTS</div>
          <h1 className="pro-hero-title">Receive your treat earnings</h1>
          <p className="pro-hero-sub">Your 90% share is paid to this account after an admin verifies it.</p>
        </header>

        {loading && (
          <div className="pro-loading-screen">
            <div className="pro-spinner" />
          </div>
        )}

        {!loading && account && (
          <div className="pro-card">
            <div className="treat-sent-head">
              <strong>Payout details</strong>
              <span className={`pro-status-pill pro-status-${account.status === "verified" ? "paid" : account.status === "rejected" ? "failed" : "pending"}`}>
                {account.status}
              </span>
            </div>
            <p className="treat-dim">{STATUS_TEXT[account.status]}</p>
            {account.status === "rejected" && account.rejectionReason && (
              <p className="pro-error">Reason: {account.rejectionReason}</p>
            )}
            <dl className="treat-confirm-list">
              <div><dt>Account holder</dt><dd>{account.accountHolderName}</dd></div>
              <div><dt>Account number</dt><dd className="treat-mono">{account.accountNumberMasked}</dd></div>
              <div><dt>IFSC</dt><dd className="treat-mono">{account.ifsc}</dd></div>
              <div><dt>Phone</dt><dd>{account.phone}</dd></div>
            </dl>
            {!editing && (
              <div className="treat-actions">
                <Link to="/my-treats" className="pro-btn pro-btn-ghost">My Received Treats</Link>
                <button type="button" className="pro-btn pro-btn-primary" onClick={() => setEditing(true)}>
                  {account.status === "rejected" ? "Resubmit details" : "Update details"}
                </button>
              </div>
            )}
          </div>
        )}

        {!loading && editing && (
          <form className="pro-card pro-form" onSubmit={submit} noValidate>
            {account && <p className="treat-dim">Submitting again sends your details back for verification.</p>}
            <label className="treat-label" htmlFor="po-name">Account holder name</label>
            <input id="po-name" className="pro-input" value={form.accountHolderName} onChange={set("accountHolderName")} maxLength={100} autoComplete="name" required />

            <label className="treat-label" htmlFor="po-acc">Bank account number</label>
            <input id="po-acc" className="pro-input" value={form.accountNumber} onChange={set("accountNumber")} inputMode="numeric" maxLength={18} autoComplete="off" required />

            <label className="treat-label" htmlFor="po-acc2">Confirm account number</label>
            <input id="po-acc2" className="pro-input" value={form.confirmAccountNumber} onChange={set("confirmAccountNumber")} inputMode="numeric" maxLength={18} autoComplete="off" onPaste={(e) => e.preventDefault()} required />

            <label className="treat-label" htmlFor="po-ifsc">IFSC code</label>
            <input id="po-ifsc" className="pro-input" value={form.ifsc} onChange={set("ifsc")} maxLength={11} placeholder="SBIN0001234" required />

            <label className="treat-label" htmlFor="po-phone">Phone number</label>
            <input id="po-phone" className="pro-input" value={form.phone} onChange={set("phone")} inputMode="tel" maxLength={14} placeholder="10-digit mobile number" required />

            {error && <p className="pro-error" role="alert">{error}</p>}

            <div className="treat-actions">
              {account && (
                <button type="button" className="pro-btn pro-btn-ghost" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </button>
              )}
              <button type="submit" className="pro-btn pro-btn-primary" disabled={saving}>
                {saving ? "Submitting…" : "Submit for verification"}
              </button>
            </div>
          </form>
        )}

        {!loading && !editing && error && <p className="pro-error">{error}</p>}
      </div>
    </div>
  );
}

export default TreatPayoutSetup;
